/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: jwt,idトークン
 * note:
 *  - JWTをパースする
 * sample:
 *  -
 * :ZNOTE:
 */

import jseu from 'js-encoding-utils';

/**
 * JWT class
 */
class JWT {
  header: any;
  payload: any;
  signature: any;
  b64uString: string;

  constructor () {
    this.header = {};
    this.payload = {};
    this.signature = new Uint8Array([]);
    this.b64uString = '';
  }

  /**
   * setter of b64uString start from here
   * @param b64uString
   */
  set setB64uString (b64uString: string) {
    const tokenArray = b64uString.split('.');
    if (tokenArray.length !== 3) throw new Error('Invalid Token');
    const decoded: any = tokenArray.map((x) => jseu.encoder.decodeBase64Url(x));

    if (decoded[0] instanceof Uint8Array) {
      this.header = JSON.parse((new TextDecoder()).decode(decoded[0]));
    } else {
      this.header = JSON.parse(decoded[0]);
    }

    if (decoded[1] instanceof Uint8Array) {
      this.payload = JSON.parse((new TextDecoder()).decode(decoded[1]));
    } else {
      this.payload = JSON.parse(decoded[1]);
    }
    this.signature = decoded[2];
    this.b64uString = JSON.parse(JSON.stringify(b64uString));
  }

  set setHeader (headerObj: { [name: string]: string }) {
    if (!headerObj.kid) throw new Error('Invalid Token Header');
    this.header = JSON.parse(JSON.stringify(headerObj));
  }

  set setPayload (payloadObj: any) {
    this.payload = JSON.parse(JSON.stringify(payloadObj));
  }

  set setSignature (signatureBuffer: Uint8Array) {
    this.signature = JSON.parse(JSON.stringify(signatureBuffer));
  }

  get getHeader () {
    return JSON.parse(JSON.stringify(this.header));
  }

  get getPayload () {
    return JSON.parse(JSON.stringify(this.payload));
  }

  get getSignature () {
    return JSON.parse(JSON.stringify(this.signature));
  }

  get getB64uString () {
    return JSON.parse(JSON.stringify(this.b64uString));

  }
}

/**
 * parse JWT
 * @param token {string}
 * @returns {JWT}
 */
export const parseJwt = (token: string): JWT => {
  const jwt: JWT = new JWT();
  jwt.setB64uString = token;
  return jwt;
};

/**
 * Get your userId from token
 * @param token {string}
 * @returns {string}
 */
export const getUserIdFromToken = (token: string): string | undefined => {
  // 0a: parse token and get userId
  const parsedToken: JWT = parseJwt(token);
  if (
    // eslint-disable-next-line no-prototype-builtins
    typeof parsedToken.payload === 'undefined' || !typeof parsedToken.payload.hasOwnProperty('cognito:username')
  ) {
    return undefined;
  }
  return parsedToken.payload['cognito:username'];
};

/**
 * Get your email from token
 * @param token {string}
 * @returns {string}
 */
export const getEmailFromToken = (token: string): string | undefined => {
  // 0a: parse token and get userId
  const parsedToken = parseJwt(token);
  if (
    typeof parsedToken.payload === 'undefined' ||
    // eslint-disable-next-line no-prototype-builtins
    !parsedToken.payload.hasOwnProperty('email')
  ) {
    return undefined;
  }
  return parsedToken.payload.email;
};

/**
 * Get the whole payload in token
 * @param token {string}
 * @returns {object}
 */
export const getAllPayload = (token: string): string | undefined => {
  // 0a: parse token and get userId
  const parsedToken = parseJwt(token);
  if (
    typeof parsedToken.payload === 'undefined') {
    return undefined;
  }
  return parsedToken.payload;
};
